import { useEffect, useState } from 'react';
import type { VideoJob } from '../services/local-video';
import { videoFailureMessage } from './video-job-diagnostics';

function elapsed(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  if (seconds < 60) return String(seconds) + 's';
  if (seconds < 3600) return Math.floor(seconds / 60) + 'm ' + seconds % 60 + 's';
  return Math.floor(seconds / 3600) + 'h ' + Math.floor((seconds % 3600) / 60) + 'm';
}

function processingLabel(job: VideoJob): string {
  if (job.failureCode === 'renderer_unavailable') return 'Renderer connection interrupted; checking again';
  if (job.phase === 'starting') return 'Preparing the video renderer';
  if (job.phase === 'starting_gpu') return 'Starting the cloud video renderer';
  if (job.phase === 'capture') {
    return job.progress === 0 ? 'Capturing frames · progress not yet reported' : 'Capturing frames · ' + job.progress + '%';
  }
  if (job.phase === 'encode') return 'Encoding MP4 · ' + job.progress + '%';
  if (job.phase === 'verify') return 'Checking MP4 · ' + job.progress + '%';
  if (job.phase === 'publish') return 'Saving MP4 · ' + job.progress + '%';
  return 'Rendering · ' + job.progress + '%';
}

/** Reports measured render progress and the age of the latest job update. */
export function VideoJobActivity({ job, cloud }: { job: VideoJob; cloud: boolean }) {
  const active = ['processing', 'queued', 'submitting'].includes(job.state);
  const capacityDelayed = job.state === 'queued' && job.phase === 'waiting_capacity';
  const allocating = job.state === 'queued' && (capacityDelayed || job.phase === 'starting_gpu');
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);

  const labels: Record<VideoJob['state'], string> = {
    cancelled: 'Render cancelled', queued: capacityDelayed ? 'Waiting for GPU capacity'
      : job.phase === 'starting_gpu' ? 'Starting the cloud video renderer' : 'Queued', submitting: 'Queuing your video',
    processing: processingLabel(job), completed: 'Your MP4 is ready',
    failed: job.failureCode === 'hardware_unavailable' ? 'The cloud renderer needs attention; your trace is saved'
      : job.failureCode === 'gpu_startup_timeout' ? 'The cloud renderer could not start; your trace is saved'
        : 'Render failed', submission_failed: 'Waiting to requeue',
    revoked: job.phase === 'expired' ? 'Download expired' : 'Video cancelled or revoked',
  };
  const status = (job.state === 'processing' && cloud) || allocating ? labels[job.state] : job.detail || labels[job.state];
  const submittedAt = Date.parse(job.createdAt || '');
  const updatedAt = Date.parse(job.updatedAt || '');
  const updateAge = now - updatedAt;
  const failure = videoFailureMessage(job);
  const capturedAll = !!job.frameProgress && job.frameProgress.total > 0 && job.frameProgress.captured === job.frameProgress.total;

  return <>
    <p role="status" className="video-job-status">{status}</p>
    {job.frameProgress && <p className="video-job-frames">Frames captured: <strong>{job.frameProgress.captured.toLocaleString('en-US')} / {job.frameProgress.total.toLocaleString('en-US')}</strong></p>}
    {failure && <p className="video-job-failure">{failure}{capturedAll ? ' All frames were captured; the MP4 export did not complete.' : ''}</p>}
    {allocating && <p className="video-job-pending">Rendering has not started. Frame capture begins after the renderer is ready.</p>}
    {capacityDelayed && <p className="video-job-pending">Your request is saved. Rendering has not started.</p>}
    {job.state === 'processing' && job.progress > 0 && <progress
      aria-label={'Rendering ' + job.title} value={job.progress} max={100} />}
    {job.state === 'processing' && job.progress === 0 && <p className="video-job-pending">
      No frame progress has been reported yet. This page checks automatically.
    </p>}
    {active && cloud && Number.isFinite(submittedAt) && <small className="video-job-timing">
      Submitted {elapsed(now - submittedAt)} ago
      {Number.isFinite(updatedAt) && (job.state === 'queued' ? ' · Last queue check was ' : ' · Last update was ') + elapsed(updateAge) + ' ago'
        + (updateAge > 90_000 ? ' · Waiting for an update' : '')}
    </small>}
  </>;
}
